import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Card from "./Card";
import BusinessInsightsChart from "./BusinessInsightsChart";
import PendingRequestsPopdown from "./PendingRequestsPopdown";
import Swal from "sweetalert2";
import "./Card.css";
import "../styles/ServiceAmount.css";

const apiUrl = process.env.REACT_APP_API_URL;

// CRITICAL FIX: Create axios instance with Authorization header interceptor for admin API calls
const adminAxios = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token to all admin API calls
adminAxios.interceptors.request.use(
  (config) => {
    // For MSIL users, use msil_id_token (contains email claims)
    // For MASS users, use sessionStorage token
    const token = localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling - redirect on 401

adminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      console.error('Unauthorized: Token expired or invalid - redirecting to login');
      
      // Clear all authentication data
      localStorage.removeItem('msil_id_token');
      localStorage.removeItem('msil_access_token');
      localStorage.removeItem('msil_code_used');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('email');
      sessionStorage.removeItem('uid');
      sessionStorage.removeItem('otpVerified');
      sessionStorage.removeItem('msil_code_used');
      
      // Redirect to login page
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Not authorized

      console.error('Forbidden: User does not have required permissions');
    }
    return Promise.reject(error);
  }
);

const DashboardContent = () => {
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showOnboardedMassDownloadModal, setShowOnboardedMassDownloadModal] = useState(false);
  const [showServiceExhaustDownloadModal, setShowServiceExhaustDownloadModal] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [revenueComparison, setRevenueComparison] = useState(null);
  const [onboardedMassComparison, setOnboardedMassComparison]=useState(null);
  const [prefetchingOnboardingReport, setPrefetchingOnboardingReport] = useState(false);
  const onboardingReportCacheRef = useRef({});




  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const userRole = sessionStorage.getItem("role");

  useEffect(() => {
    // CRITICAL FIX: Wait for authentication to be ready before fetching data
    // Check if token and role are available
    const msilToken = localStorage.getItem('msil_id_token');
    const massToken = sessionStorage.getItem('token');
    const currentToken = msilToken || massToken;
    const currentRole = sessionStorage.getItem('role');

    if (currentToken && currentRole) {
      // Authentication ready, fetch data
      fetchDashboardData();
      fetchPendingRequests();
      fetchRevenueComparison();
      fetchOnboardedMassComparison();
    } else {
      // Wait a bit and check again (for MSIL login flow)
      const checkAuth = setInterval(() => {
        const token = localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');
        const role = sessionStorage.getItem('role');
        if (token && role) {
          clearInterval(checkAuth);
          fetchDashboardData();
          fetchPendingRequests();
          fetchRevenueComparison();
          fetchOnboardedMassComparison();
        }
      }, 100);

      // Clear interval after 5 seconds to avoid infinite loop
      const stopWaiting = setTimeout(() => {
        clearInterval(checkAuth);
        // No auth after 1.5s: fetch anyway so finally() sets loading false (avoids infinite "Loading dashboard...")
        fetchDashboardData();
        fetchPendingRequests();
        fetchRevenueComparison();
        fetchOnboardedMassComparison();
      }, 1500);


return () => {
        clearInterval(checkAuth);
        clearTimeout(stopWaiting);
      };
        }
  }, []);

  const fetchDashboardData = async () => {
    try {
      // VAPT FIX: Use generic admin data endpoint to hide internal API structure
      const res = await adminAxios.post(`/api/v1/admin/data`, { type: "dashboard" });

      setDashboardData(res.data.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Authentication/authorization error - component will handle redirect
        setLoading(false);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

const fetchRevenueComparison = async () => {
    try {
      // VAPT FIX: Use generic admin data endpoint to hide internal API structure
      const res = await adminAxios.post(`/api/v1/admin/data`, { type: "revenueComparison" });

      console.log("Revenue Comparison Data:", JSON.stringify(res.data.data));

      setRevenueComparison(res.data.data);
    } catch (error) {
      console.error("Error fetching revenue comparison data:", error);
    }
  }

  const fetchOnboardedMassComparison=async() =>{
    try{
        // VAPT FIX: Use generic admin data endpoint to hide internal API structure
        const res=await adminAxios.post(`/api/v1/admin/data`, { type: "onboardedMassComparison" });

        console.log("---------comparison Data String--------:", JSON.stringify(res.data.data));
        setOnboardedMassComparison(res.data.data);
    }
    catch(error){
        console.error("Error fetching revenue comparison",error);
    }
  }

  const getOnboardingFilename = (normalized) =>
    normalized === "excel" || normalized === "xlsx"
      ? "onboarding-status-report.xlsx"
      : "onboarding-status-report.pdf";

  const getOnboardingReportBlob = async (format, { allowCache = true } = {}) => {
    const normalized = String(format).toLowerCase();
    const url = `${apiUrl}/api/v1/report/onboardingStatusReport?format=${normalized}`;
    const cached = onboardingReportCacheRef.current[normalized];

    if (allowCache && cached) {
      return cached;
    }

    const res = await adminAxios.get(url, { responseType: "blob" });
    const blob = res.data;
    onboardingReportCacheRef.current[normalized] = blob;
    return blob;
  };

  useEffect(() => {
    if (!showOnboardedMassDownloadModal) return;

    // Prefetch the most common format so user click triggers instant download.
    const prefetchExcel = async () => {
      setPrefetchingOnboardingReport(true);
      try {
        await getOnboardingReportBlob("excel");
      } catch (error) {
        console.warn("Onboarding report prefetch failed:", error);
      } finally {
        setPrefetchingOnboardingReport(false);
      }
    };

    prefetchExcel();
  }, [showOnboardedMassDownloadModal]);

  const downloadOnboardedMassReport = async (format) => {
    const normalized = String(format).toLowerCase();
    const filename = getOnboardingFilename(normalized);

    try {
      const blob = await getOnboardingReportBlob(normalized);
      const objectUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);

      setShowOnboardedMassDownloadModal(false);
    } catch (e) {
      console.error("Failed to download onboarding report:", e);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Unable to download onboarding report. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const downloadServiceAboutToExhaustReport = async () => {
    const filename = "service-about-to-exhaust-report.xlsx";
    const url = `${apiUrl}/api/v1/report/serviceAboutToExhaustReport`;

    try {
      const res = await adminAxios.get(url, { responseType: "blob" });
      const objectUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      setShowServiceExhaustDownloadModal(false);
    } catch (e) {
      console.error("Failed to download service about to exhaust report:", e);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Unable to download service about to exhaust report. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  const handleCardClick = (title) => {
    if (title === t("PENDING_REQUESTS")) {
      if (userRole === "SUPER_ADMIN") {
        setShowPendingModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Access Denied",
          text: "You do not have permission to view this section.",
          confirmButtonText: "OK",
        });
      }
    }

    if (title === t("TOTAL_ONBOARDED_MASS")) {
      setShowOnboardedMassDownloadModal(true);
    }

    if (title === t("SERVICE_ABOUT_TO_EXHAUST")) {
      setShowServiceExhaustDownloadModal(true);
    }
  };



  const fetchPendingRequests = async () => {
    try {
      // VAPT FIX: Use generic admin data endpoint to hide internal API structure
      const res = await adminAxios.post(`/api/v1/admin/data`, { type: "pendingRequests" });

      const normalizedRequests = res.data.data.map((req, index) => {
        const rawStatus = (req.dealerShipStatus || "").toString().trim();

        let status = "approved";
        if (rawStatus === "0") {
          status = "pending";
        } else if (rawStatus === "2") {
          status = "denied";
        }

        return {
          id: index + 1,
          parentGroup: req.dealerShipInfo?.PARENT_GROUP || "",
          dealerMapCode: req.dealerShipInfo?.DEALER_MAP_CODE || "",
          locationCode: req.dealerShipInfo?.LOC_CODE || "",
          name: req.dealerShipInfo?.DEALERSHIP_NAME || "",
          code: req.dealerShipInfo?.MUL_SRV_DEALER_CODE || "",
          region: req.dealerShipInfo?.REGION_CODE || "",
          date: req.onBoardingDate,
          status,
        };
      });

      setPendingRequests(normalizedRequests);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    }
  };

  // const handleCardClick = (title) => {
  //   if (title === t("PENDING_REQUESTS")) {
  //     setShowPendingModal(true);
  //   }
  // };

  if (loading) return <p>Loading dashboard...</p>;

  // Compute pending count dynamically
  const pendingCount =
    pendingRequests.filter((req) => req.status === "pending" || req.status === "denied").length || 0;

  const cardData = [
    {
      title: t("TOTAL_MASS_DMS"),
      value: dashboardData?.getTotalDms || "0",
      linkText: t("VIEW_TOTAL_MASS_USERS_DMS"),
    },
    // {
    //   title: t("TOTAL_ONBOARDED_MASS"),
    //   value: dashboardData?.getTotalOnboardedMass || "0",
    //   linkText: t("VIEW_TOTAL_ONBOARDED_MASS"),
    //   additionalInfo: t("UP_FROM_LAST_MONTH"),
    //   valueColor: "text-success",
    // },

      {
    title: t("TOTAL_ONBOARDED_MASS"),
    value: dashboardData?.getTotalOnboardedMass || "0",
    linkText: t("VIEW_TOTAL_ONBOARDED_MASS"),
    additionalInfo: onboardedMassComparison
      ? `${onboardedMassComparison.percentageChange.toFixed(2)}% of total MASS`
      : "Loading...",
    valueColor: "text-success", // Always green since it's a positive metric
  },
    {
      title: t("TOTAL_MASS_ACTIVE"),
      value: dashboardData?.getActiveMass || "0",
      linkText: t("VIEW_ALL_ACTIVE"),
    },
    {
      title: t("PENDING_REQUESTS"),
      value: pendingCount.toString(), // dynamic pending count
      linkText: t("MANAGE_REQUESTS"),
    },
    {
      title: t("SERVICE_ABOUT_TO_EXHAUST"),
      value: dashboardData?.getMassCountWithServiceAboutToExhaust || "0",
      linkText: t("VIEW_SERVICE_ABOUT_TO_EXHAUST"),
      valueColor: "text-danger",
    },
    {
      title: t("ACTIVE_SERVICE_PLANS"),
      value: dashboardData?.getActivePlans || "0",
      linkText: t("VIEW_ALL_SERVICE_PLANS"),
    },
    // {
    //   title: t("TOTAL_REVENUE"),
    //   value: dashboardData?.getTotalRevenue
    //     ? Math.floor(parseFloat(dashboardData.getTotalRevenue))
    //     : "0",
    //   linkText: t("VIEW_REPORT"),
    //   additionalInfo: t("UP_FROM_LAST_QUARTER"),
    //   valueColor: "text-success",
    // },
    {
  title: t("TOTAL_REVENUE"),
  value: dashboardData?.getTotalRevenue
    ? Math.floor(parseFloat(dashboardData.getTotalRevenue))
    : "0",
  linkText: t("VIEW_REPORT"),
  additionalInfo: revenueComparison
    ? `${revenueComparison.percentageChange >= 0 ? "▲" : "▼"}
       ${Math.abs(revenueComparison.percentageChange).toFixed(1)}%
       ${revenueComparison.percentageChange >= 0 ? "Up" : "Down"} from last quarter`
    : "Loading...",
  valueColor: revenueComparison?.percentageChange >= 0 ? "text-success" : "text-danger",
}
,
  ];

  return (
    <div className="row g-3" style={{ paddingLeft: "30px" }}>
      {cardData.map((data, index) => (
        <div key={index} className="col-md-3 d-flex" onClick={() => handleCardClick(data.title)}>
          <Card {...data} />
        </div>
      ))}

      <BusinessInsightsChart totalRevenue={dashboardData?.getTotalRevenue || 0} />

      {userRole === "SUPER_ADMIN" && (
  <PendingRequestsPopdown
    show={showPendingModal}
    onClose={() => setShowPendingModal(false)}
    requests={pendingRequests}
    onUpdateRequests={setPendingRequests} // Updates pending requests dynamically
  />
)}

      {showOnboardedMassDownloadModal && (
        <div className="popup-overlay" onClick={() => setShowOnboardedMassDownloadModal(false)}>
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "520px" }}
          >
            <div className="popup-header">
              <h5 className="popup-title">{t("VIEW_TOTAL_ONBOARDED_MASS")}</h5>
              <button
                className="popup-close"
                onClick={() => setShowOnboardedMassDownloadModal(false)}
              >
                X
              </button>
            </div>
            <div className="popup-body">
              <p>Select the format to download the updated onboarding list:</p>
              {prefetchingOnboardingReport && (
                <p style={{ fontSize: "13px", color: "#6c757d", marginBottom: 0 }}>
                  Preparing report for faster download...
                </p>
              )}
            </div>
            <div className="popup-footer" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button className="btn btn-primary" onClick={() => downloadOnboardedMassReport("pdf")}>
                PDF
              </button>
              <button className="btn btn-secondary" onClick={() => downloadOnboardedMassReport("excel")}>
                Excel
              </button>
            </div>
          </div>
        </div>
      )}

      {showServiceExhaustDownloadModal && (
        <div className="popup-overlay" onClick={() => setShowServiceExhaustDownloadModal(false)}>
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "520px" }}
          >
            <div className="popup-header">
              <h5 className="popup-title">{t("VIEW_SERVICE_ABOUT_TO_EXHAUST")}</h5>
              <button
                className="popup-close"
                onClick={() => setShowServiceExhaustDownloadModal(false)}
              >
                X
              </button>
            </div>
            <div className="popup-body">
              <p>Download the excel report for MASS users with service usage above 90%.</p>
            </div>
            <div className="popup-footer" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={downloadServiceAboutToExhaustReport}>
                Excel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardContent;